<script lang="tsx">
  import { defineComponent, type PropType } from "vue";
  import { Button, ButtonGroup, Menu } from "primevue";
  import { useObjectUrl } from "@vueuse/core";

  import { LControl, LControlScale, LMap, LMarker, LTileLayer, LTooltip } from "@maxel01/vue-leaflet";
  // @ts-expect-error as of writing, @types/leaflet does not have type definitions for v2 yet!
  import { Icon as LIcon } from "leaflet";
  import "leaflet-css";

  import { icon as faRender } from '@fortawesome/fontawesome-svg-core';
  import { FontAwesomeIcon as Icon } from "@fortawesome/vue-fontawesome";
  import { faArrowUpRightFromSquare, faDiamondTurnRight, faLocationDot } from "@fortawesome/free-solid-svg-icons";


  function search_params(params: Record<string, string | number | undefined | null>) {
    const query = new URLSearchParams();
    for (const key in params) {
      if (params.hasOwnProperty(key)) {
        if (params[key] != null) {
          query.set(key, params[key].toString());
        }
      }
    }
    return query.toString();
  }

  export default defineComponent({
    components: {
      Button, Icon, Menu, ButtonGroup,
      LMap, LTileLayer, LControlScale, LMarker, LTooltip, LControl,
    },
    props: {
      site: {
        type: String as PropType<"enca" | "frca">,
        required: true,
      },
      placeName: String,
      address: String,
      longitude: { type: Number, required: true },
      latitude: { type: Number, required: true },
    },
    setup() {
      // fa's icons need the namespace added in order to be used standalone
      const iconRoot = faRender(faLocationDot).node[0];
      iconRoot.setAttribute("xmlns", "http://www.w3.org/2000/svg");
      const svgData = iconRoot.outerHTML;

      return {
        faLocationDotURL: useObjectUrl(
          new Blob([ svgData ], { type: "image/svg+xml" })
        )
      };
    },
    data() {
      return {
        faArrowUpRightFromSquare,
        faDiamondTurnRight,
        Menu,

        mapZoom: 17,
        mapCenter: [this.latitude, this.longitude],

        mapMarkerIcon: new LIcon({
          iconUrl: this.faLocationDotURL,
          iconSize: [30, 30], iconAnchor: [15, 30],
        }),
      };
    },
    computed: {
      externalMapMenu() {
        return [
          {
            label: "Open in:",
            items: [
              {
                label: "Google Maps",
                target: "_blank",
                url: `https://google.com/maps/search/?${search_params({
                  api: 1,
                  query: this.address || `${this.latitude}, ${this.longitude}`,
                  center: `${this.latitude},${this.longitude}`,
                  zoom: this.mapZoom,
                })}`,
              },
              {
                label: "Apple Maps",
                target: "_blank",
                url: `https://maps.apple.com/place?${search_params({
                  coordinate: `${this.latitude},${this.longitude}`,
                  address: this.address || undefined,
                  name: this.placeName ? this.placeName : undefined,
                })}`,
              },
              {
                label: "OpenStreetMap",
                target: "_blank",
                url: this.address ? (
                  `https://www.openstreetmap.org/search?${search_params({
                    query: this.address,
                    mlat: this.latitude,
                    mlon: this.longitude,
                    zoom: this.mapZoom,
                  })}`
                ) : (
                  `https://www.openstreetmap.org/?${search_params({
                    mlat: this.latitude,
                    mlon: this.longitude,
                    zoom: this.mapZoom,
                  })}`
                ),
              },
              {
                label: "Other",
                target: "_blank",
                url: `geo:${this.latitude},${this.longitude}?${search_params({
                  q: this.address ? `${this.latitude},${this.longitude}(${this.address})` : undefined,
                  z: this.mapZoom,
                })}`,
              },
            ],
          }
        ];
      },
      directionsMenu() {
        return [
          {
            label: "View directions in:",
            items: [
              {
                label: "Google Maps",
                target: "_blank",
                url: `https://google.com/maps/dir/?${search_params({
                  api: 1,
                  destination: this.address || `${this.latitude}, ${this.longitude}`,
                  center: `${this.latitude},${this.longitude}`,
                  zoom: this.mapZoom,
                  travelmode: "driving",
                })}`,
              },
              {
                label: "Apple Maps",
                target: "_blank",
                url: `https://maps.apple.com/directions?${search_params({
                  destination: this.address || `${this.latitude}, ${this.longitude}`,
                  mode: "driving"
                })}`,
              },
              {
                label: "OpenStreetMap",
                target: "_blank",
                url: `https://www.openstreetmap.org/directions?${search_params({
                  from: "",
                  to: this.address || `${this.latitude}, ${this.longitude}`,
                  engine: "fossgis_osrm_car",
                  zoom: this.mapZoom,
                })}`,
              }
            ],
          }
        ];
      }
    },
  });
</script>

<template>
  <div class="map">
    <LMap class="map-leaflet" v-model:zoom="mapZoom"
      v-model:center="mapCenter">
      <LTileLayer layer-type="base" name="OpenStreetMap"
        url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
        attribution="&copy; <a href='https://www.openstreetmap.org/copyright'>OpenStreetMap</a> contributors"/>
      <LControlScale position="bottomleft" :updateWhenIdle="false" />

      <LControl position="topright">
        <ButtonGroup>
          <Button size="large" label="Open map" icon-pos="right"
            @click="($refs.externalMapMenu as InstanceType<typeof Menu>).toggle($event)">
            <template #icon="slot">
              <Icon :class="[slot.class]"
                :icon="faArrowUpRightFromSquare" style="font-size: 1rem"/>
            </template>
          </Button>
          <Button size="large" label="Directions" icon-pos="right"
            @click="($refs.directions as InstanceType<typeof Menu>).toggle($event)">
            <template #icon="slot">
              <Icon :class="[slot.class]"
                :icon="faDiamondTurnRight" style="font-size: 1rem"/>
            </template>
          </Button>
        </ButtonGroup>
      </LControl>

      <LMarker :lat-lng="[latitude, longitude]" :icon="mapMarkerIcon">
        <LTooltip v-if="placeName" :options="{direction: 'top', offset: [0, -30]}">{{ placeName }}</LTooltip>
      </LMarker>
    </LMap>

    <Menu ref="externalMapMenu" :model="externalMapMenu" :popup="true" />
    <Menu ref="directions" :model="directionsMenu" :popup="true" />
  </div>
</template>

<style lang="less" scoped>
  .map {
    display: flex;
    flex-direction: column;

    .map-leaflet {
      flex: 1 0 auto;
      align-self: stretch;

      border: 1px solid var(--p-content-border-color);
      border-radius: var(--p-content-border-radius);
    }
  }

  // leaflet overrides the browser font :(
  :deep(.leaflet-container) {
    font-family: unset;
    font-size: 0.75rem;
    line-height: inherit;
  }
</style>
